import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "@/lib/adminApi";

export default function AdminQuizResults() {
  const { quizId = "" } = useParams();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const attempts = useQuery({
    queryKey: ["admin-attempts", quizId, search],
    queryFn: () => adminApi.listAttempts(quizId, { search, pageSize: 100 }),
  });

  const bulkDelete = useMutation({
    mutationFn: () => adminApi.bulkDelete(quizId),
    onSuccess: () => {
      setConfirmBulkDelete(false);
      qc.invalidateQueries({ queryKey: ["admin-attempts", quizId] });
    },
  });

  return (
    <main className="mx-auto max-w-6xl p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-brand-ink">
            {attempts.data?.quizTitle ?? "Loading…"}
          </h1>
          <div className="text-sm text-brand-ink/60">
            {attempts.data ? `${attempts.data.totalCount} attempts` : ""}
          </div>
        </div>
        <div className="flex gap-2">
          <a className="btn-secondary" href={adminApi.resultsExcelUrl(quizId)}>Export .xlsx</a>
          <button className="btn-secondary text-brand" onClick={() => setConfirmBulkDelete(true)}>
            Bulk delete
          </button>
        </div>
      </div>

      <input
        className="field-input mb-4 max-w-sm"
        placeholder="Search by name / email / mobile"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-light text-brand-ink">
              <tr>
                {["Candidate", "Email", "State", "Score", "Corr/Wrong/Skip", "Violations", "ID", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {attempts.data?.items.map((a) => (
                <tr key={a.attemptId} className="border-t border-brand-ink/10">
                  <td className="px-3 py-2">
                    <div>{a.candidateName}</div>
                    <div className="text-xs text-brand-ink/60">{a.mobile}</div>
                  </td>
                  <td className="px-3 py-2">{a.email}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-pill px-2 py-0.5 text-xs
                      ${a.state === "Submitted" || a.state === "AutoSubmitted" ? "bg-emerald-100 text-emerald-800"
                        : a.state === "Disqualified" ? "bg-brand-light text-brand"
                        : "bg-yellow-100 text-yellow-800"}`}>
                      {a.state}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-semibold">
                    {a.finalScore}
                    {a.passFail && (
                      <span className={`ml-2 text-xs ${a.passFail === "Pass" ? "text-emerald-700" : "text-brand"}`}>
                        {a.passFail}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{a.correct} / {a.wrong} / {a.skipped}</td>
                  <td className="px-3 py-2">{a.violationCount}</td>
                  <td className="px-3 py-2">{a.idVerificationStatus}</td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/admin/attempts/${a.attemptId}`} className="text-brand hover:underline">
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
              {attempts.data?.items.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-brand-ink/60">
                  No attempts yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {confirmBulkDelete && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-brand">Delete all attempts for this quiz?</h3>
            <p className="mt-2 text-sm text-brand-ink/80">
              This permanently removes every attempt, answer, violation, camera snapshot and ID
              document for this quiz. This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setConfirmBulkDelete(false)}>Cancel</button>
              <button
                className="btn-primary bg-brand"
                onClick={() => bulkDelete.mutate()}
                disabled={bulkDelete.isPending}>
                {bulkDelete.isPending ? "Deleting…" : "Delete everything"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
